import { useSyncExternalStore } from 'react';
import { match } from 'ts-pattern';
import type { AuthUser, UserRole } from '@/lib/auth';
import { useAuth } from './use-auth';

export type Experience = 'storefront' | 'self-service';

/** Mesmo padrão do `ThemeToggle` (`clearance-theme`). */
const STORAGE_KEY = 'clearance-experience';

const listeners = new Set<() => void>();

/** Sem sessão não há preferência a gravar — e um `throw` aqui obrigaria todo
 *  consumidor a um ramo defensivo para um caso que a guarda já cobre. */
function noop(): void {
  return;
}

function subscribe(onChange: () => void): () => void {
  listeners.add(onChange);
  return () => {
    listeners.delete(onChange);
  };
}

function readPreference(): Experience | null {
  if (typeof window === 'undefined') return null;
  const raw = window.localStorage.getItem(STORAGE_KEY);
  return raw === 'storefront' || raw === 'self-service' ? raw : null;
}

/** Grava a preferência e acorda todos os consumidores do hook. */
export function setExperiencePreference(value: Experience): void {
  window.localStorage.setItem(STORAGE_KEY, value);
  for (const listener of listeners) listener();
}

/**
 * A experiência default de cada papel.
 *
 * `supplier` tem braço explícito, e não é decoração: ele nunca chega aqui
 * (`hasPlatformAccess` barra em `RequireAuth`), mas o `.exhaustive()` recusa
 * três braços e um `.otherwise()` engoliria um papel novo em silêncio.
 */
function defaultFor(role: UserRole): Experience {
  return match(role)
    .with('buyer', () => 'storefront' as const)
    .with('admin', () => 'storefront' as const)
    .with('employee', () => 'self-service' as const)
    .with('supplier', () => 'self-service' as const)
    .exhaustive();
}

/** Só o admin escolhe; para os outros a preferência gravada é ignorada. */
function canToggleFor(user: AuthUser | null): boolean {
  return user?.role === 'admin';
}

export interface ExperienceState {
  /** `null` **sem sessão** — é o que permite montar o hook fora do ramo
   *  protegido sem inventar uma experiência para `user: null`. */
  experience: Experience | null;
  canToggle: boolean;
  setExperience: (value: Experience) => void;
}

/**
 * Qual das duas experiências este usuário vê.
 *
 * A alternância é **preferência, não permissão**: ela decide para onde o
 * toggle leva, e nada mais. A vitrine continua acessível a qualquer papel de
 * plataforma nos dois modos — um employee que receba o link de um produto o
 * abre. Fechar a porta produziria 404 para uma URL que a pessoa tem permissão
 * de ver.
 */
export function useExperience(): ExperienceState {
  const { user } = useAuth();
  const stored = useSyncExternalStore(subscribe, readPreference, () => null);

  if (!user?.role) {
    return { experience: null, canToggle: false, setExperience: noop };
  }

  const canToggle = canToggleFor(user);
  return {
    experience: canToggle ? stored ?? defaultFor(user.role) : defaultFor(user.role),
    canToggle,
    setExperience: setExperiencePreference,
  };
}
